
"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import { MagneticButton } from "./MagneticButton";
import heroStudent from "@/assets/hero-student.jpg";
import p1 from "@/assets/parent-1.jpg";
import p2 from "@/assets/parent-2.jpg";
import p3 from "@/assets/parent-3.jpg";
import Image from "next/image";
import { CountUp } from "../CountUp";

export function Hero() {
  const ref = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden bg-cream pt-28 pb-20 md:pt-36 md:pb-28">
      <div className="absolute inset-0 bg-gold-radial pointer-events-none" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:px-10 lg:grid-cols-12 lg:gap-10">
        {/* Copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white/70 px-4 py-1.5 text-xs font-medium text-navy backdrop-blur"
          >
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            Now accepting applications · Class of 2026
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-balance font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-navy md:text-7xl lg:text-[5.25rem]"
          >
            The Future of{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Atherton</span>
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{ originX: 0 }}
                className="absolute -bottom-2 left-0 h-3 w-full rounded-full bg-gold/70"
              />
            </span>{" "}
            Starts Here.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-7 max-w-xl font-serif-body text-xl leading-relaxed text-muted-foreground md:text-2xl"
          >
            Expert-led education designed for the next generation of leaders —
            where rigorous academics meet the freedom to wonder.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <MagneticButton href="#enroll">
              Apply for Enrollment
              <ArrowRight className="h-4 w-4" />
            </MagneticButton>

            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {[p1, p2, p3].map((img, i) => (
                  <div
                    key={i}
                    className="relative h-9 w-9 overflow-hidden rounded-full border-2 border-white bg-cream"
                  >
                    <Image
                      src={img}
                      alt={`Parent ${i + 1}`}
                      fill
                      className="object-cover"
                    />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5 text-gold">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  4.9 from <strong className="text-navy">1,200+</strong> families
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Image */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
              <Image
                src={heroStudent}
                alt="A confident student stepping into her future"
                width={1280}
                height={1600}
                className="absolute inset-0 h-full w-full object-cover will-change-transform"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
            </div>

            {/* Floating stat card */}
            <motion.div
              initial={{ opacity: 0, x: -30, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7 }}
              className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-[var(--shadow-card)] md:block"
            >
              <p className="font-display text-3xl font-extrabold text-navy"><CountUp value="98%" /></p>
              <p className="mt-1 max-w-[140px] text-xs text-muted-foreground">
                Graduates accepted into top-tier universities
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30, y: -20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="absolute -top-5 -right-4 hidden rounded-2xl bg-navy p-4 text-primary-foreground shadow-[var(--shadow-card)] md:block"
            >
              <p className="font-serif-body text-xs italic">Ranked #1</p>
              <p className="font-display text-sm font-bold">Private K-12, 2025</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
